import { noddiAsync, URLKeys } from 'noddi-async';
import { BrandType } from 'noddi-types';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiDialog, NoddiSeparator, Popover, PopoverContent, PopoverTrigger } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import {
  ApiBaseUrlOverrideCard,
  ImpersonateUserDialog,
  setEnvFavicon,
  useAuthContext,
  useSetShowEnvBanner,
  useSetShowEnvFavicon,
  useSetShowSlotAvailability,
  useShowEnvBanner,
  useShowEnvFavicon,
  useShowSlotAvailability
} from 'noddi-web';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOnImpersonated } from '../../hooks/useOnImpersonated';
import { useCurrentBrand, useSetBrand } from '../../stores/BrandStore/brandStore';
import { BrandThemePicker } from './BrandThemePicker';

const POINT_OF_SALE_PAYMENT_OPTIONS = [
  { id: 23, name: 'Rosten' },
  { id: 22, name: 'Leangen' }
];

export const DevSettingsSelector = () => {
  const { currentBrand } = useCurrentBrand();
  const setBrand = useSetBrand();
  const { impersonatedUser, logout, userData } = useAuthContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [backendOpen, setBackendOpen] = useState(false);
  const showEnvBanner = useShowEnvBanner();
  const setShowEnvBanner = useSetShowEnvBanner();
  const showEnvFavicon = useShowEnvFavicon();
  const setShowEnvFavicon = useSetShowEnvFavicon();
  const showSlotAvailability = useShowSlotAvailability();
  const setShowSlotAvailability = useSetShowSlotAvailability();
  const onImpersonated = useOnImpersonated();

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'fixed right-1 z-docked rounded-lg bg-white p-2 shadow-lg lg:right-5',
            impersonatedUser ? 'top-16 md:top-20' : 'top-4 lg:top-10'
          )}
          title='Dev settings'
        >
          <NoddiIcon name='Settings' size='small' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-56 p-0' align='end'>
        <div className='px-2 py-1.5 text-xs font-medium text-muted-foreground'>Theme</div>
        <BrandThemePicker currentBrand={currentBrand} onSelect={setBrand} onDone={() => setOpen(false)} />
        {userData?.user.isSuperuser && (
          <BrandInsightsRow brandDomain={currentBrand} onNavigate={() => setOpen(false)} />
        )}
        <NoddiSeparator />
        <div className='px-2 py-1.5 text-xs font-medium text-muted-foreground'>Display</div>
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- environment banner toggle row in the dev-only settings popover */}
        <div
          className='flex cursor-pointer items-center px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground'
          onClick={() => setShowEnvBanner(!showEnvBanner)}
        >
          <NoddiIcon size='small' className={cn('mr-2', showEnvBanner ? 'opacity-100' : 'opacity-0')} name='Check' />
          Environment banner
        </div>
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- environment favicon toggle row in the dev-only settings popover */}
        <div
          className='flex cursor-pointer items-center px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground'
          onClick={() => {
            const next = !showEnvFavicon;
            setShowEnvFavicon(next);
            setEnvFavicon(next);
          }}
        >
          <NoddiIcon size='small' className={cn('mr-2', showEnvFavicon ? 'opacity-100' : 'opacity-0')} name='Check' />
          Environment favicon
        </div>
        <NoddiSeparator />
        <div className='px-2 py-1.5 text-xs font-medium text-muted-foreground'>Feature flags</div>
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- debug data flag toggle row in the dev-only settings popover */}
        <div
          className='flex cursor-pointer items-center px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground'
          onClick={() => setShowSlotAvailability(!showSlotAvailability)}
        >
          <NoddiIcon
            size='small'
            className={cn('mr-2', showSlotAvailability ? 'opacity-100' : 'opacity-0')}
            name='Check'
          />
          Debug data
        </div>
        <NoddiSeparator />
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backend URL row in the dev-only settings popover, opening the backend picker */}
        <div
          className='flex cursor-pointer items-center px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground'
          onClick={() => {
            setOpen(false);
            setBackendOpen(true);
          }}
        >
          <NoddiIcon size='small' className='mr-2' name='Globe' />
          Backend URL
        </div>
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- impersonate user row in the dev-only settings popover */}
        <div
          className='flex cursor-pointer items-center px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground'
          onClick={() => {
            setOpen(false);
            setImpersonateOpen(true);
          }}
        >
          <NoddiIcon size='small' className='mr-2' name='Users' />
          Impersonate user
        </div>
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- log out row in the dev-only settings popover */}
        <div
          className='flex cursor-pointer items-center px-2 py-1.5 text-sm text-destructive hover:bg-accent hover:text-destructive'
          onClick={() => {
            setOpen(false);
            // oxlint-disable-next-line typescript/no-floating-promises -- logout() awaits only cancelQueries() and never rejects
            logout();
          }}
        >
          <NoddiIcon size='small' className='mr-2' name='LogOut' />
          Log out
        </div>
        <NoddiSeparator />
        <div className='px-2 py-1.5 text-xs font-medium text-muted-foreground'>Ad-hoc payment</div>
        {POINT_OF_SALE_PAYMENT_OPTIONS.map((option) => (
          // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- ad-hoc payment option row in the dev-only settings popover
          <div
            key={option.id}
            className='flex cursor-pointer items-center px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground'
            onClick={() => {
              setOpen(false);
              navigate(customerRoutes.pointOfSalePayment.getPath({ serviceDepartmentId: option.id }));
            }}
          >
            <NoddiIcon size='small' className='mr-2' name='CreditCard' />
            {option.name}
          </div>
        ))}
      </PopoverContent>
      <NoddiDialog
        open={impersonateOpen}
        onClose={() => setImpersonateOpen(false)}
        title='Impersonate user'
        description='Select a user from the list to view the application as them and take actions on their behalf'
        maxWidth='lg'
        className='h-[85vh] max-h-[85vh]'
        contentClassName='flex min-h-0 flex-1 flex-col overflow-hidden'
      >
        <ImpersonateUserDialog
          onClose={() => setImpersonateOpen(false)}
          onImpersonated={onImpersonated}
          defaultBrandDomain={currentBrand}
        />
      </NoddiDialog>
      <NoddiDialog
        open={backendOpen}
        onClose={() => setBackendOpen(false)}
        title='Backend URL'
        description='Route this app at a different API. Useful on deploy previews to point at a local backend.'
        maxWidth='sm'
      >
        <ApiBaseUrlOverrideCard />
      </NoddiDialog>
    </Popover>
  );
};

/**
 * Links to the selected brand's insights page. Superuser only (the link endpoint is), and hidden while
 * the brand has no link yet. The brand record is already cached by the app shell; reopening the
 * popover refetches a lookup that failed.
 */
const BrandInsightsRow = ({ brandDomain, onNavigate }: { brandDomain: BrandType; onNavigate: () => void }) => {
  const { data: brand, error: brandError } = noddiAsync.useGet({
    type: URLKeys.getByDomainBrand,
    input: { brandDomain }
  });
  const { data: link, error: linkError } = noddiAsync.useGet({
    type: URLKeys.getBrandInsightsLink,
    input: { brandId: brand?.id ?? 0 },
    queryConfig: { enabled: Boolean(brand) }
  });

  if (brandError ?? linkError) {
    return <div className='px-2 py-1.5 text-sm text-muted-foreground'>Insights link unavailable</div>;
  }
  const insightsToken = link?.insightsToken;
  if (!insightsToken) {
    return null;
  }
  return (
    <Link
      to={customerRoutes.brandInsights.getPath({ insightsToken })}
      onClick={onNavigate}
      className='flex items-center px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-hidden'
    >
      <NoddiIcon size='small' className='mr-2' name='TrendingUp' />
      Insights page
    </Link>
  );
};
